<template>
  <div class="my-4 mx-6">
    <div class="mb-6">
      <router-link class="breadcrumb-link" to="/publisher/reports">
        Reports
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-link">Top Developers</span>
    </div>
    <div
      class="TopDevelopersReport__header d-flex flex-column flex-sm-row justify-space-between align-sm-center gap-3 pa-0 mt-4"
    >
      <div class="d-flex flex-column flex-sm-row align-sm-center gap-3">
        <v-select
          v-model="filter.sortBy"
          :items="sortOptions"
          item-text="label"
          item-value="value"
          class="TopDevelopersReport__select-filter flex-grow-0"
          height="40"
          dense
          dark
          hide-details
          :readonly="reportReady"
          @click="displayReadonlyAlert"
        />
        <v-select
          v-model="filter.limit"
          :items="limits"
          item-text="label"
          item-value="value"
          class="TopDevelopersReport__select-filter flex-grow-0"
          height="40"
          dense
          dark
          hide-details
          :readonly="reportReady"
          @click="displayReadonlyAlert"
        />
        <v-select
          v-model="filter.period"
          :items="periods"
          item-text="label"
          item-value="value"
          class="TopDevelopersReport__select-filter flex-grow-0"
          height="40"
          dense
          dark
          hide-details
          :readonly="reportReady"
          @click="displayReadonlyAlert"
        />
        <RangePicker
          v-if="filter.period == 'custom'"
          :start-date.sync="filter.startDate"
          :end-date.sync="filter.endDate"
          :read-only="reportReady"
          :click="displayReadonlyAlert"
        />
      </div>
      <div class="d-flex" v-if="reportReady">
        <v-btn
          class="v-btn primary-btn mr-4"
          @click="onDowloadReport"
          elevation="2"
          large
        >
          <v-icon dark left> mdi-download </v-icon>
          Download
        </v-btn>
        <v-btn
          class="v-btn reset-btn"
          @click="onResetFilter"
          elevation="2"
          large
        >
          <v-icon dark left> mdi-refresh </v-icon>
          Reset
        </v-btn>
      </div>
      <v-btn
        class="v-btn primary-btn"
        @click="onRunReport"
        elevation="2"
        large
        v-else
      >
        <v-icon dark left> mdi-play </v-icon>
        Run
      </v-btn>
    </div>
    <div class="TopDevelopersReport__body">
      <v-row v-if="reportReady" class="mt-4">
        <v-col>
          <v-data-table
            :headers="headers"
            :items="tableData"
            ref="dataTable"
            class="data-table"
            @click:row="handleClickDeveloperRow"
          >
            <template v-slot:item.rank="{ item }">
              <span class="font-weight-bold">#{{ item.rank }}</span>
            </template>
            <template v-slot:item.installs_count="{ item }">
              {{ item.installs_count.toLocaleString() }}
            </template>
            <template v-slot:item.plugins_count="{ item }">
              {{ item.plugins_count.toLocaleString() }}
            </template>
            <template v-slot:item.average_installs="{ item }">
              {{ item.average_installs.toFixed(1) }}
            </template>
            <template v-slot:item.verified="{ item }">
              <v-icon
                v-if="item.verified"
                color="success"
                small
                title="Verified Developer"
              >
                mdi-check-circle
              </v-icon>
              <v-icon v-else color="grey" small title="Not Verified">
                mdi-circle-outline
              </v-icon>
            </template>
            <template v-slot:item.developer_status="{ item }">
              <v-chip
                :color="item.developer_status === 'active' ? 'success' : 'grey'"
                small
                dark
              >
                {{ item.developer_status }}
              </v-chip>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <div v-if="loading" class="spinner">
        <FlamePreloader :compact="true" :size="100" />
      </div>
    </div>
  </div>
</template>

<script>
import FlamePreloader from '@/components/_Common/FlamePreloader'
import RangePicker from './RangePicker.vue'
import jsPDF from 'jspdf'
import { getFirstMondayAndLastSunday } from '@/utils/common'
import marketplaceAPI from '@/services/marketplace'
import 'jspdf-autotable'

export default {
  name: 'TopDevelopersReport',
  components: {
    RangePicker,
    FlamePreloader,
  },
  data() {
    const periods = [
      { value: 'all', label: 'All Time' },
      { value: 'custom', label: 'Custom Range' },
    ]

    const limits = [10, 20, 50, 100].map((value) => ({
      value,
      label: 'Top ' + value,
    }))

    const [firstMonday, lastSunday] = getFirstMondayAndLastSunday()

    return {
      periods,
      limits,
      sortOptions: [
        { value: 'installsCount', label: 'Sort By Installs Count' },
        { value: 'rating', label: 'Sort By Rating' },
        { value: 'revenue', label: 'Sort By Revenue', disabled: true },
      ],
      filter: {
        period: 'all',
        sortBy: 'installsCount',
        limit: 10,
        startDate: firstMonday,
        endDate: lastSunday,
      },
      data: [],
      loading: false,
      reportReady: false,
      tableData: [],
      reportGenerationId: null, // Store generation ID for potential PDF download
    }
  },
  computed: {
    headers() {
      return [
        {
          text: 'Rank',
          align: 'center',
          sortable: false,
          value: 'rank',
          width: '80px',
        },
        {
          text: 'Name',
          align: 'start',
          sortable: false,
          value: 'name',
        },
        { text: 'Email', value: 'email' },
        {
          text: 'Installs',
          value: 'installs_count',
          align: 'end',
          sortable: false,
        },
        {
          text: 'Plugins',
          value: 'plugins_count',
          align: 'end',
          sortable: false,
        },
        {
          text: 'Avg Installs',
          value: 'average_installs',
          align: 'end',
          sortable: false,
        },
        {
          text: 'Verified',
          value: 'verified',
          align: 'center',
          sortable: false,
          width: '100px',
        },
        {
          text: 'Status',
          value: 'developer_status',
          align: 'center',
          sortable: false,
          width: '100px',
        },
      ]
    },
  },
  methods: {
    /**
     * Convert date to ISO 8601 format
     * @param {Date|string} date - Date to convert
     * @returns {string} ISO 8601 formatted date string
     */
    formatDateForAPI(date) {
      if (!date) return null
      const d = new Date(date)
      return d.toISOString()
    },

    /**
     * Map old sortBy values to new API field names
     * @param {string} sortBy - Old sortBy value
     * @returns {string} New API field name
     */
    mapSortField(sortBy) {
      const mapping = {
        installsCount: 'installs_count',
        rating: 'plugins_count', // Rating not available, use plugins_count as fallback
        revenue: 'installs_count', // Revenue not available, use installs_count as fallback
      }
      return mapping[sortBy] || 'installs_count'
    },

    // Main Action part of the component.
    // Run button click event handler
    async onRunReport() {
      this.loading = true

      try {
        // Build filters
        const filters = {
          developer_status: 'all',
          verified_status: 'all',
          top_n: this.filter.limit,
        }

        // Set time range
        if (this.filter.period === 'custom') {
          if (!this.filter.startDate || !this.filter.endDate) {
            alert('Please select both start and end date.')
            this.loading = false
            return
          }
          filters.time_range = 'custom'
          filters.start_date = this.formatDateForAPI(this.filter.startDate)
          filters.end_date = this.formatDateForAPI(this.filter.endDate)
        } else {
          filters.time_range = 'all_time'
        }

        // Build sort options
        const sort = {
          field: this.mapSortField(this.filter.sortBy),
          direction: 'desc',
        }

        // Build pagination
        const pagination = {
          page: 1,
          per_page: this.filter.limit,
        }

        // Generate report
        const result = await marketplaceAPI.generateTopDevelopersReport({
          filters,
          sort,
          pagination,
          generate_pdf: false, // Set to true for async PDF generation (requires polling)
        })

        // Store generation ID for potential PDF download
        if (result && result.report_generation_id) {
          this.reportGenerationId = result.report_generation_id
        }

        // Transform API response to match existing component structure
        if (result && result.data && result.data.developers) {
          // Store full data with all fields from API
          this.data = result.data.developers.map((dev) => ({
            rank: dev.rank,
            developer_id: dev.developer_id,
            id: dev.developer_id, // Map developer_id to id for compatibility
            name: dev.name,
            email: dev.email,
            installs_count: dev.installs_count || 0,
            plugins_count: dev.plugins_count || 0,
            average_installs: dev.average_installs || 0,
            developer_status: dev.developer_status,
            verified: dev.verified,
          }))

          // Map to table data format for display
          this.tableData = this.data.map((dev) => ({
            id: dev.developer_id,
            developer_id: dev.developer_id,
            name: dev.name,
            email: dev.email,
            rank: dev.rank,
            installs_count: dev.installs_count,
            plugins_count: dev.plugins_count,
            average_installs: dev.average_installs,
            verified: dev.verified,
            developer_status: dev.developer_status,
          }))

          this.reportReady = true
        } else {
          console.error('Unexpected API response format:', result)
          alert('Failed to generate report. Please try again.')
        }
      } catch (error) {
        console.error('Error generating report:', error)
        alert(
          `Failed to generate report: ${error.message || 'Unknown error'}`
        )
      } finally {
        this.loading = false
      }
    },

    // Download button click event handler
    async onDowloadReport() {
      const source = this.$refs['dataTable']

      // Build column headers with all relevant fields
      const columnHeader = [
        'Rank',
        'Name',
        'Email',
        'Installs',
        'Plugins',
        'Avg Installs',
        'Verified',
        'Status',
      ]

      // Build rows with all data
      const rows = source.items.map((element) => [
        element.rank || '',
        element.name || '',
        element.email || '',
        element.installs_count || 0,
        element.plugins_count || 0,
        element.average_installs ? element.average_installs.toFixed(1) : '0.0',
        element.verified ? 'Yes' : 'No',
        element.developer_status || '',
      ])

      const pdfName = 'Top Developers Report'
      const doc = new jsPDF()
      doc.autoTable(columnHeader, rows, {
        startY: 10,
        headStyles: { fillColor: [66, 139, 202] },
        styles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 20 }, // Rank
          1: { cellWidth: 50 }, // Name
          2: { cellWidth: 50 }, // Email
          3: { cellWidth: 25, halign: 'right' }, // Installs
          4: { cellWidth: 25, halign: 'right' }, // Plugins
          5: { cellWidth: 30, halign: 'right' }, // Avg Installs
          6: { cellWidth: 25, halign: 'center' }, // Verified
          7: { cellWidth: 25, halign: 'center' }, // Status
        },
      })
      doc.save(pdfName + '.pdf')
    },

    // Reset Button Click event handler
    onResetFilter() {
      this.reportReady = false
      this.loading = false
      this.reportGenerationId = null
    },

    /**
     * Download PDF from server (if available)
     * This is an alternative to client-side PDF generation
     * Requires reportGenerationId and polling for PDF completion
     */
    async downloadServerPDF() {
      if (!this.reportGenerationId) {
        alert('No report generation available. Please generate a report first.')
        return
      }

      try {
        const blob = await marketplaceAPI.downloadReportPDF(
          this.reportGenerationId
        )
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'Top Developers Report.pdf'
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } catch (error) {
        console.error('Failed to download PDF:', error)
        alert(
          `PDF download failed: ${error.message || 'Unknown error'}. Falling back to client-side generation.`
        )
        // Fallback to client-side PDF generation
        this.onDowloadReport()
      }
    },

    displayReadonlyAlert() {
      if (this.reportReady)
        alert(
          'Filters are readonly and please click on Reset button to restart filter.'
        )
    },

    handleClickDeveloperRow(developer) {
      // Use developer_id from API response, fallback to id
      const developerId = developer.developer_id || developer.id
      this.$router.push({
        name: 'developer_detail',
        params: { developerId },
      })
    },
  },
}
</script>
<style lang="scss">
$style: TopDevelopersReport;
.#{$style} {
  padding: 42px 24px 24px;
  &__row {
    margin-bottom: 58px;
  }

  &__header {
    .v-btn.primary-btn {
      background-color: $B1;
      color: white;
    }
    .v-btn.reset-btn {
      background-color: $R1;
      color: white;
    }

    .date-picker {
      border-radius: 8px;
      padding: 8px 10px;
      background-color: hsla(0, 0%, 100%, 0.08);
      border: 1px solid #5e636d;
      margin-right: 10px;
    }
  }

  &__select-filter {
    border: 1px solid #5e636d;
    border-radius: 8px;
    padding: 0 10px;
    background-color: hsla(0, 0%, 100%, 0.08);
    @media screen and (min-width: 600px) {
      width: 200px;
    }
  }

  &__body {
    min-height: 500px;
    position: relative;
    .spinner {
      position: absolute;
      top: 50%;
      left: 50%;
      margin-top: -50px;
      margin-left: -50px;
      width: 100px;
      height: 100px;
    }
  }
}
</style>
